/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: hierarchical-metadata-modal-component.ts
 *
 * Created by randysorensen on 11/30/17.
 */

'use strict';


function HierarchicalMetadataModalController(
        this: JS5Object,

        _: JS5Object,
        $q: JS5Object,
        $location: JS5Object,
        $anchorScroll: JS5Function,
        $filter: JS5Function,

        PermissionsService: JS5Object,
        MetadataService: JS5Object,
        PartnerPackagesService: JS5Object,

        NetworkResourceFactory: JS5Object,
        ContentResourceFactory: JS5Object) {

    const PACKAGE_ADVANCED_METADATA_VIEW_STATE = 'PACKAGE_ADVANCED_METADATA_VIEW_STATE';

    let typeDefs: JS5Object;
    let typeList: string[];


    //======//
    // Data //
    //======//

    const getStaticMetadata = () => {
        let promises: JS5Object = {
            metaService: MetadataService.propertiesPromise,
        };

        promises.network = NetworkResourceFactory.get({
            id: this.resolve.packageObject.content.network.id
        }).$promise;

        promises.content = ContentResourceFactory.get({
            id: this.resolve.packageObject.content.id
        }).$promise;

        if (this.resolve.packageObject.season) {
            promises.season = ContentResourceFactory.getSeason({
                id: this.resolve.packageObject.season.id
            }).$promise;
        }

        if (this.resolve.packageObject.volume) {
            promises.volume = ContentResourceFactory.getVolume({
                id: this.resolve.packageObject.volume.id
            }).$promise;
        }

        $q.all(promises).then((viewData: JS5Object) => {
            _.forEach(viewData, (view: JS5Object, key: string) => {
                if (key !== 'metaService') {
                    if (view.metadata) {
                        view.metadata = MetadataService.buildCustomComponentMap(view.metadata.properties);
                    }
                    this.viewData[key] = view;
                    this.viewData[key].show = true;
                }
            });
        });
    };

    const createTypeDefs = (packageObject: JS5Object) => {
        typeDefs = <JS5Object>{};

        //
        // Properties in these typeDefs:
        //
        //      factory:
        //          the resource factory where the partner's
        //          metadata items originate
        //
        //      method:
        //          the resource factory method to use
        //
        //      id:
        //          the id criteria to use when requesting the
        //          resource
        //
        //      seasonSubscription:
        //          if undefined, this resource is applicable to
        //          both season and volume subscriptions;
        //
        //          if true, this resource is only applicable to
        //          season subscriptions;
        //
        //          if false, this resource is only applicable to
        //          volume subscriptions.
        //
        typeDefs.network = {
            factory: NetworkResourceFactory,
            method: 'get',
            id: packageObject.content.network.id,
            seasonSubscription: undefined,
        };

        typeDefs.content = {
            factory: ContentResourceFactory,
            method: 'get',
            id: packageObject.content.id,
            seasonSubscription: undefined,
        };

        if (packageObject.season) {
            typeDefs.season = {
                factory: ContentResourceFactory,
                method: 'getSeason',
                id: packageObject.season.id,
                seasonSubscription: true,
            };
        }
        if (packageObject.volume) {
            typeDefs.volume = {
                factory: ContentResourceFactory,
                method: 'getVolume',
                id: packageObject.volume.id,
                seasonSubscription: false,
            };
        }

        typeList = _.keys(typeDefs);
    };

    const getPartnerItemValues = (partnerPackages: JS5Object[]) => {
        const partnerPackageLookup = _.keyBy(partnerPackages, 'partner.name');
        this.partnerList = _.map(partnerPackages, 'partner');

        // get each partner's MVPD item values for each type
        let partnerMVPDPromises: JS5Object = {};
        _.forEach(typeDefs, (typeDef: JS5Object, typeName: string) => {
            partnerMVPDPromises[typeName] = <JS5Object>{};
            _.forEach(this.partnerList, (partner: JS5Object) => {
                if (typeDef.seasonSubscription === undefined ||
                    typeDef.seasonSubscription === partnerPackageLookup[partner.name].subscription.seasonSubscription)
                {
                    partnerMVPDPromises[typeName][partner.name] = typeDef.factory[typeDef.method]({
                        id: typeDef.id,
                        mvpdPartnerId: partner.id,
                    }).$promise;
                }
            });
        });

        return $q.all({
            network: $q.all(partnerMVPDPromises.network),
            content: $q.all(partnerMVPDPromises.content),
            season: $q.all(partnerMVPDPromises.season),
            volume: $q.all(partnerMVPDPromises.volume),
        });
    };

    const transformPartnerItemValues = (mvpdItemsByTypeAndPartner: JS5Object) => {
        let partnerHasMVPDItems = <JS5Object>{};

        // build a by-partner item lookup for each
        // metadata type
        _.forEach(typeList, (type: string) => {
            _.forEach(mvpdItemsByTypeAndPartner[type], (partnerMVPDItems: JS5Object, partnerName: string) => {
                if (_.keys(_.get(partnerMVPDItems, 'metadata.properties')).length > 0) {
                    partnerHasMVPDItems[partnerName] = true;
                    this.partnerMVPDProperties[type][partnerName] =
                        MetadataService.buildCustomComponentMap(partnerMVPDItems.metadata.properties, [], true);
                }
            });
        });

        // partner list gets trimmed to only partners who have
        // MVPD data
        _.remove(this.partnerList, (partner: JS5Object) => {
            return !partnerHasMVPDItems[partner.name];
        });
    };

    const getMVPDMetadata = () => {
        return MetadataService.propertiesPromise
            .then(() => {
                // get list of partners relevant to this package
                return PartnerPackagesService.queryPartnerPackages(
                    this.resolve.packageObject.id,
                    this.resolve.packageObject
                );
            })
            .then(getPartnerItemValues)
            .then(transformPartnerItemValues);
    };

    const getPermissions = () => {
        return PermissionsService.canRead(PACKAGE_ADVANCED_METADATA_VIEW_STATE);
    };


    //================//
    // Template Logic //
    //================//

    this.getMetadataValue = (item: JS5Object) => {
        let result = <any>'';

        if (item && item.instanceData) {
            switch (item.uiControlHint) {
                case 'CheckboxList':
                    // Multi Picklists
                    result = _
                        .chain(item.picklistItems)
                        .filter((picklistItem: JS5Object) => {
                            return _.includes(item.instanceData.value, picklistItem.id)
                        })
                        .map('displayName')
                        .join(', ')
                        .value();
                    break;

                case 'DropDownList':
                case 'List':
                    // Single Picklists
                    result = _.filter(item.picklistItems, (picklistItem: JS5Object) => {
                        return picklistItem.id === item.instanceData.value
                    });
                    result = result.length > 0 ? result[0].displayName : '';
                    break;

                case 'DateField':
                case 'DateFieldEnhanced':
                case 'DateTimeField':
                case 'DateTimeFieldEnhanced':
                    result = $filter('amDateFormat')(item.instanceData.value, 'MMMM DD, YYYY, h:mm A');
                    break;

                case 'TextListInput':
                    // Dynamic Picklists
                    result = _
                        .chain(item.instanceData.value)
                        .map('stringValue')
                        .join('|')
                        .value();
                    break;

                default:
                    // All others
                    result = item.instanceData.value;
            }
        }

        return result;
    };

    this.onNavigate = ($event: JS5Object, anchorId: string) => {
        $event.target.blur();
        $location.hash(anchorId);
        $anchorScroll();
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        this.viewData = {
            network: <JS5Object>{},
            content: <JS5Object>{},
            season: <JS5Object>{},
            volume: <JS5Object>{},
        };

        this.partnerMVPDProperties = {
            network: <JS5Object>{},
            content: <JS5Object>{},
            season: <JS5Object>{},
            volume: <JS5Object>{},
        };

        this.loadingIndicator.show = true;

        // this promise chain could probably be reworked to be
        // clearer...
        getPermissions()
            .then((perms: JS5Object) => {
                if (perms) {
                    getStaticMetadata();
                    createTypeDefs(this.resolve.packageObject);
                    getMVPDMetadata()
                        .then(() => {
                            // select partner 0 by default
                            if (this.partnerList.length > 0) {
                                this.selectedPartner = this.partnerList[0].name;
                            }
                            this.loadingIndicator.show = false;
                        });
                }
            });
    };

    this.$onDestroy = () => {
        // clear the anchor so the scrollbar starts at the top the
        // next time the modal is opened
        $location.hash(null);
    }
}


angular
.module('prism-ui.package')
.component(
    'hierarchicalMetadataModal', {
    controller: HierarchicalMetadataModalController,
    templateUrl: 'package/editor/components/modals/hierarchicalMetadata/hierarchical-metadata-modal-template.html',
    bindings: {
        resolve: '<',
        close: '&',
        dismiss: '&',
    },
});
